type ModelRates = { input: number; cacheRead: number; cacheWrite: number; output: number };

// USD per million tokens (Anthropic list prices). Matched on model family so
// every Opus/Sonnet version we run through Bedrock resolves without frontend changes.
const OPUS_RATES: ModelRates = { input: 15, cacheRead: 1.5, cacheWrite: 18.75, output: 75 };
const SONNET_RATES: ModelRates = { input: 3, cacheRead: 0.3, cacheWrite: 3.75, output: 15 };

const MODEL_PRICING: { match: string; rates: ModelRates }[] = [
	{ match: 'opus', rates: OPUS_RATES },
	{ match: 'sonnet', rates: SONNET_RATES },
];

export interface IModelUsage {
	model?: string | null;
	inputTokens?: number | null;
	cachedInputTokens?: number | null;
	cacheWriteTokens?: number | null;
	outputTokens?: number | null;
}

export function estimatePromptCostUsd(usage: IModelUsage): number | undefined {
	const normalized = usage.model?.toLowerCase();
	const rates = normalized
		? MODEL_PRICING.find((entry) => normalized.includes(entry.match))?.rates
		: undefined;

	if (!rates) {
		return undefined;
	}

	return (
		((usage.inputTokens ?? 0) * rates.input +
			(usage.cachedInputTokens ?? 0) * rates.cacheRead +
			(usage.cacheWriteTokens ?? 0) * rates.cacheWrite +
			(usage.outputTokens ?? 0) * rates.output) /
		1_000_000
	);
}

export interface PromptModelUsage extends IModelUsage {
	promptCount: number;
}

export interface PromptCostSummary {
	totalCostUsd: number;
	costPerPromptUsd: number | null;
	pricedPromptCount: number;
	unpricedPromptCount: number;
}

export interface WorkspacePromptCost {
	workspaceID: string;
	estimatedCostUsd: number;
	interactionCount: number;
}

export function summarizePromptUsageCosts(rows: PromptModelUsage[]): PromptCostSummary {
	let totalCostUsd = 0;
	let pricedPromptCount = 0;
	let unpricedPromptCount = 0;

	for (const row of rows) {
		const cost = estimatePromptCostUsd(row);
		if (cost == null) {
			unpricedPromptCount += row.promptCount;
			continue;
		}
		totalCostUsd += cost;
		pricedPromptCount += row.promptCount;
	}

	return {
		totalCostUsd,
		pricedPromptCount,
		unpricedPromptCount,
		costPerPromptUsd: pricedPromptCount === 0 ? null : totalCostUsd / pricedPromptCount,
	};
}

export function rankWorkspacesByCost(
	workspaces: { workspaceID: string; byModel: PromptModelUsage[] }[],
	limit = 10,
): WorkspacePromptCost[] {
	return workspaces
		.map((workspace) => {
			const summary = summarizePromptUsageCosts(workspace.byModel);
			const interactionCount = workspace.byModel.reduce((total, row) => total + row.promptCount, 0);
			return {
				workspaceID: workspace.workspaceID,
				estimatedCostUsd: summary.totalCostUsd,
				interactionCount,
			};
		})
		.filter((workspace) => workspace.estimatedCostUsd > 0)
		.sort((a, b) => {
			if (b.estimatedCostUsd !== a.estimatedCostUsd) {
				return b.estimatedCostUsd - a.estimatedCostUsd;
			}
			return a.workspaceID.localeCompare(b.workspaceID);
		})
		.slice(0, limit);
}
